import { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { Segmented, Slider, Tooltip } from 'antd';
import {
    PlayCircleFilled,
    PauseCircleFilled,
    LoadingOutlined,
    FastBackwardOutlined,
    FastForwardOutlined,
} from '@ant-design/icons';
import { getFormattedSeekValue } from "src/utils/helperFunctions";

// Question types where speaking IS the answer — speech there is expected; speech
// on any other type is the flag. (Mirrors VideoPlayer.)
const SPEECH_QUESTION_TYPES = ["speech_scripted", "speech_spontaneous"]
const SKIP_SEC = 10

/**
 * Audio-only proctoring player. Plays the audio chunks (each an independent webm,
 * same as the video chunks) back-to-back on a single timeline built from the
 * chunks' real recording-clock offsets (startTime/endTime), and plots VAD speech
 * markers by `startTimeMs` on that same clock. Used when video proctoring is off.
 *
 * Exposes `playSegment(startSec, endSec)` and `pause()` through the forwarded
 * ref so the parent's marker list can play just one unexpected-speech region.
 */
// Shortest segment we will play from a marker click — timeupdate ticks ~4x/s,
// so anything shorter than this would stop before it was audible.
const MIN_SEGMENT_SEC = 0.5

const AudioPlayer = forwardRef(({ loading, audioChunks, audioEvents = [], onSegmentStop }, ref) => {
    const audioRef = useRef()
    const seekWithinRef = useRef(null) // seconds to seek within a chunk once it can play
    // When a marker segment is playing, the recording-clock second at which we
    // auto-pause. null = normal free playback.
    const segmentEndRef = useRef(null)
    // Mirror of `isPlaying` that event handlers can read without a stale closure —
    // `canplay` fires after every seek too, not just on chunk change, so a stale
    // `true` here would restart playback after the user pressed pause.
    const isPlayingRef = useRef(false)
    const [chunkIndex, setChunkIndex] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)

    const setPlaying = (value) => {
        isPlayingRef.current = value
        setIsPlaying(value)
    }

    const play = (el) => {
        if (!el) return
        el.playbackRate = playbackRate
        const p = el.play()
        if (p && typeof p.catch === "function") p.catch(() => { })
    }
    const [globalSec, setGlobalSec] = useState(0)
    const [playbackRate, setPlaybackRate] = useState(1)
    const [canPlayLoading, setCanPlayLoading] = useState(false)

    const chunks = useMemo(
        () => [...(audioChunks || [])].sort((a, b) => a.startTime - b.startTime),
        [audioChunks]
    )
    // Total timeline = last chunk's end offset on the recording clock (falls back
    // to the summed durations if endTime is missing).
    const totalSec = useMemo(() => {
        if (!chunks.length) return 0
        const last = chunks[chunks.length - 1]
        return last.endTime || chunks.reduce((a, c) => a + (c.duration || 0), 0)
    }, [chunks])

    const vadMarkers = useMemo(() => {
        if (!totalSec || !audioEvents?.length) return []
        return audioEvents.map((event) => {
            const startSec = (event.startTimeMs || 0) / 1000
            const durSec = Math.max((event.durationMs || 0) / 1000, 0)
            const questionType = event.questionType || null
            return {
                startSec,
                durSec,
                questionIndex: event.questionIndex ?? null,
                leftPct: Math.min((startSec / totalSec) * 100, 100),
                widthPct: Math.min(Math.max((durSec / totalSec) * 100, 0.8), 100),
                flagged: !!questionType && !SPEECH_QUESTION_TYPES.includes(questionType),
            }
        })
    }, [audioEvents, totalSec])

    // On chunk change: apply playback rate, seek to the pending within-chunk offset,
    // and resume playing if we were playing.
    useEffect(() => {
        const el = audioRef.current
        if (!el) return
        const handleCanPlay = () => {
            el.playbackRate = playbackRate
            if (seekWithinRef.current != null) {
                el.currentTime = seekWithinRef.current
                seekWithinRef.current = null
            }
            setCanPlayLoading(false)
            if (isPlayingRef.current && el.paused) play(el)
        }
        el.addEventListener("canplay", handleCanPlay)
        return () => el.removeEventListener("canplay", handleCanPlay)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chunkIndex])

    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = playbackRate
    }, [playbackRate])

    // Leave segment mode (marker playback). Called when the segment reaches its
    // end or when the user takes over with the normal controls.
    const clearSegment = () => {
        if (segmentEndRef.current == null) return
        segmentEndRef.current = null
        if (typeof onSegmentStop === "function") onSegmentStop()
    }

    const handleTimeUpdate = () => {
        const chunk = chunks[chunkIndex]
        const el = audioRef.current
        if (!chunk || !el) return
        const exactSec = chunk.startTime + el.currentTime
        setGlobalSec(chunk.startTime + Math.floor(el.currentTime))
        if (segmentEndRef.current != null && exactSec >= segmentEndRef.current) {
            el.pause()
            setPlaying(false)
            clearSegment()
        }
    }

    const handleEnded = () => {
        if (chunkIndex < chunks.length - 1) {
            setCanPlayLoading(true)
            setChunkIndex((i) => i + 1)
        } else {
            setPlaying(false)
            clearSegment()
        }
    }

    // Returns true when the seek stayed within the current chunk (element is
    // ready to play now), false when a chunk switch was scheduled.
    const seekTo = (targetSec) => {
        if (!chunks.length) return false
        const target = Math.max(0, Math.min(targetSec, totalSec))
        let ci = chunks.findIndex((c) => target >= c.startTime && target <= c.endTime)
        if (ci < 0) ci = chunks.length - 1
        setGlobalSec(target)
        const within = Math.max(0, target - chunks[ci].startTime)
        if (ci === chunkIndex && audioRef.current) {
            audioRef.current.currentTime = within
            return true
        }
        seekWithinRef.current = within
        setCanPlayLoading(true)
        setChunkIndex(ci)
        return false
    }

    const pause = () => {
        const el = audioRef.current
        setPlaying(false)
        if (el && !el.paused) el.pause()
    }

    // Controls for the marker list:
    //  - playSegment(start, end): seek to `start` and play until `end` (recording
    //    clock seconds), then auto-pause and report back via `onSegmentStop`.
    //  - pause(): stop the current segment where it is.
    // When the seek switches chunks we only flag "playing" — the new chunk's
    // `canplay` handler starts it, so we never call play() on the element that
    // is about to swap its src.
    useImperativeHandle(ref, () => ({
        playSegment: (startSec, endSec) => {
            const end = Math.max(Number(endSec) || 0, startSec + MIN_SEGMENT_SEC)
            segmentEndRef.current = Math.min(end, totalSec || end)
            const sameChunk = seekTo(startSec)
            setPlaying(true)
            if (sameChunk) play(audioRef.current)
        },
        pause: () => {
            pause()
            clearSegment()
        },
    }), [seekTo, playbackRate, totalSec])

    // Manual controls always exit segment mode — the reviewer has taken over.
    const playPause = () => {
        const el = audioRef.current
        if (!el) return
        clearSegment()
        if (el.paused) {
            setPlaying(true)
            play(el)
        } else {
            pause()
        }
    }

    const handleUserSeek = (sec) => {
        clearSegment()
        seekTo(sec)
    }

    if (loading) {
        return (
            <div className="audio-player-loading flex items-center justify-center">
                <LoadingOutlined />
            </div>
        )
    }
    if (chunks.length === 0) {
        return <p className="text-18 weight-600 clr-gray mt-20">No audio found</p>
    }

    return (
        <div className="audio-proctoring-player mt-10">
            <audio
                ref={audioRef}
                src={chunks[chunkIndex]?.link}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
            />
            <Slider
                tooltip={{ formatter: getFormattedSeekValue }}
                min={0}
                max={totalSec}
                value={globalSec}
                onChange={handleUserSeek}
            />
            {
                vadMarkers.length > 0
                    ? <div className="vad-markers-container">
                        <div className="vad-markers-track">
                            {vadMarkers.map((marker, index) =>
                                <Tooltip
                                    key={index}
                                    title={`Speech detected · ${getFormattedSeekValue(marker.startSec)} · ${marker.durSec.toFixed(1)}s${marker.questionIndex != null ? ` · Q${marker.questionIndex}` : " · outside the quiz"}${marker.flagged ? " · non-speech question" : ""}`}
                                >
                                    <span
                                        className={`vad-marker${marker.flagged ? " vad-marker-flagged" : ""}`}
                                        style={{ left: `${marker.leftPct}%`, width: `${marker.widthPct}%` }}
                                        onClick={() => handleUserSeek(marker.startSec)}
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <p className="vad-markers-legend text-12 flex items-center">
                            <span className="vad-legend-swatch" />
                            Speech detected
                            {vadMarkers.some(m => m.flagged) && (
                                <>
                                    <span className="vad-legend-swatch vad-legend-swatch-flagged" />
                                    Speech on a non-speech question
                                </>
                            )}
                        </p>
                    </div>
                    : <></>
            }
            <div className={`controls-container w-80 flex items-center justify-between ${canPlayLoading ? "opacity-6" : ""}`}>
                <div className="flex items-center">
                    <button className="default-btn flex items-center" onClick={playPause}>
                        {!isPlaying ? <PlayCircleFilled /> : <PauseCircleFilled />}
                    </button>
                    <span
                        onClick={() => handleUserSeek(globalSec - SKIP_SEC)}
                        className={`skip-btn cursor-pointer ml-14 ${globalSec < SKIP_SEC ? "disabled opacity-6" : ""}`}
                    >
                        <FastBackwardOutlined />
                    </span>
                    <p className="text-14 ml-10 mr-10">{getFormattedSeekValue(globalSec)}</p>
                    <span
                        onClick={() => handleUserSeek(globalSec + SKIP_SEC)}
                        className={`skip-btn cursor-pointer ${globalSec >= totalSec - SKIP_SEC ? "disabled opacity-6" : ""}`}
                    >
                        <FastForwardOutlined />
                    </span>
                </div>
                <Segmented
                    options={[{ label: "1x", value: 1 }, { label: "1.5x", value: 1.5 }, { label: "2x", value: 2 }]}
                    value={playbackRate}
                    onChange={setPlaybackRate}
                />
            </div>
        </div>
    )
})

AudioPlayer.displayName = "AudioPlayer"

export default AudioPlayer
